"use client";

import { SkeletonBlock, SkeletonLine, Typography, WidgetErrorCard } from "@health/ui";
import { splitHighlights } from "@health/validation/text";
import { useEffect, useRef } from "react";

/** `show_claim` tool-input schema. Exported so the footer extractor
 * (`ClaimWidget.footer.ts`) can consume the same shape — single source
 * of truth for the widget + its satellite projections. */
export type ClaimPayload = {
  id: string;
  claim: string;
  /** Compliance disclaimer — rendered at the group footer, not inline. */
  disclaimer: string | null;
  highlights?: string[];
};

export type ClaimInput = {
  claim_id: string;
  headline: string;
  claim: ClaimPayload;
};

type Props = {
  input: Record<string, unknown>;
  output?: unknown;
  onSubmit: (result: unknown) => void;
  submitted?: boolean;
  /** True when the conversation has moved past this claim (any user msg after). */
  historic?: boolean;
};

function HighlightedText({ text, highlights }: { text: string; highlights?: string[] }) {
  if (!highlights?.length) return <>{text}</>;
  const segments = splitHighlights(text, highlights);
  return (
    <>
      {segments.map((seg, i) =>
        seg.highlighted ? (
          <span key={i} className="text-[var(--color-claim-highlight)]">
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
}

export function ClaimWidget({ input, onSubmit, submitted }: Props) {
  const { claim_id, headline, claim } = input as unknown as ClaimInput;
  const didSubmit = useRef(false);
  const isValid = !!claim?.claim && claim.id === claim_id;

  useEffect(() => {
    if (!submitted && !didSubmit.current && isValid) {
      didSubmit.current = true;
      onSubmit({ acknowledged: true });
    }
  }, [submitted, onSubmit, isValid]);

  if (!isValid) {
    return <WidgetErrorCard>Claim not available</WidgetErrorCard>;
  }

  return (
    <div className="rounded-2xl pb-10">
      {headline && <Typography.AgentText className="mb-6">{headline}</Typography.AgentText>}

      <p
        className="text-[26px] leading-[1.3] font-normal text-[var(--color-text-primary)]"
        style={{ fontFamily: "var(--font-display)" }}
      >
        <HighlightedText text={claim.claim} highlights={claim.highlights} />
        {claim.disclaimer && <strong>*</strong>}
      </p>
    </div>
  );
}

/**
 * Loading skeleton mirroring ClaimWidget — two blocks sized to the real
 * widget's typography so hydration doesn't shift the layout:
 *   - headline (22px display, mb-6)
 *   - claim copy (26px display × ~3 wrapped rows)
 * The disclaimer lives at the group footer (see `ChatMessageGroup
 * .footerItems`) and isn't part of the widget skeleton.
 */
export function ClaimWidgetSkeleton() {
  return (
    <div className="rounded-2xl pb-10 w-full">
      <SkeletonLine $size="heading" $width="md" className="mb-6" />
      <SkeletonBlock $radius="md" className="h-[100px] w-[92%]" />
    </div>
  );
}
